import { useCallback } from 'react';
import { SMSTag } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { EntityType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { InboxTag } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { SMSDataV4, SMSDataSharedUtils } from '@frontend/api-sms-data';
import { emitOpenVoicemailInsightPanel } from '@frontend/inbox-call-item-event-bus';
import { GlobalPanel } from '@frontend/panel-engine';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

// TODO: remove this mapping once BE sends the correct EntityType in the event
const mapEventEntityTypeToInboxTagEntityType = (entityType: string): EntityType => {
  switch (entityType) {
    case 'SMS':
    case 'ENTITY_TYPE_SMS':
      return EntityType.ENTITY_TYPE_SMS;
    case 'CALL_RECORD':
    case 'ENTITY_TYPE_CALL':
      return EntityType.ENTITY_TYPE_CALL;
    case 'ENTITY_TYPE_VOICEMAIL':
      return EntityType.ENTITY_TYPE_VOICEMAIL;
    default:
      return EntityType.ENTITY_TYPE_UNSPECIFIED;
  }
};

const APPLIED_BY_FALLBACK = '00000000-0000-0000-0000-000000000000';

/**
 * A hook that returns a function to execute when an INBOX_THREAD_ITEM_TAG event is received.
 */
export const useInboxThreadItemTagWSHandler = () => {
  const { upsertUnifiedThread, upsertThreadItem } = SMSDataV4._QueryUpdaters.useUnifiedThreadUpdater();
  const { isSmsMessageItem, isCallRecordItem } = SMSDataSharedUtils.threadItemsUtils;

  const wsEventHandler = useCallback<GetWebsocketEventHandler<NotificationType.INBOX_THREAD_ITEM_TAG>>(
    async (event) => {
      const { params } = event;
      const locationId = ('locationID' in event && event.locationID) as string;

      const threadItemTag: InboxTag = {
        entityId: params.entityId,
        tagId: params.tagId,
        entityCreatedAt: params.entityCreatedAt || '',
        entityType: mapEventEntityTypeToInboxTagEntityType(params.entityType),
        appliedBy: params.appliedBy || APPLIED_BY_FALLBACK,
      };

      switch (params.updateType) {
        case 'ADDED':
          // update tags array in thread
          await upsertUnifiedThread({
            matchValues: {
              threadId: params.threadId,
              locationId,
            },
            updater: (oldThreadData) => {
              if (!oldThreadData) return oldThreadData;
              const updatedUniqueTags = SMSDataSharedUtils.dedupeInboxTagsArray([
                ...(oldThreadData.uniqueTags || []),
                threadItemTag,
              ]);
              return {
                ...oldThreadData,
                uniqueTags: updatedUniqueTags,
              };
            },
            isOptimistic: false,
          });
          // update tags array in threadItem
          await upsertThreadItem({
            matchValues: {
              threadId: params.threadId,
              locationId,
              threadItemId: params.threadCallId || params.entityId,
            },
            updater: (oldThreadItemData) => {
              if (!oldThreadItemData) return oldThreadItemData;
              if (isSmsMessageItem(oldThreadItemData) && oldThreadItemData.smsMessage) {
                const smsTag: SMSTag = {
                  tagId: params.tagId,
                  smsId: params.entityId,
                  appliedBy: params.appliedBy || APPLIED_BY_FALLBACK,
                  smsCreatedAt: params.entityCreatedAt || '',
                };
                return {
                  smsMessage: {
                    ...oldThreadItemData.smsMessage,
                    tagsDetailed: SMSDataSharedUtils.dedupeSMSTagsArray([
                      ...(oldThreadItemData.smsMessage.tagsDetailed || []),
                      smsTag,
                    ]),
                    tags: Array.from(new Set([...oldThreadItemData.smsMessage.tags, params.tagId])),
                  },
                };
              }
              if (isCallRecordItem(oldThreadItemData) && oldThreadItemData.callRecord) {
                return {
                  callRecord: {
                    ...oldThreadItemData.callRecord,
                    tags: SMSDataSharedUtils.dedupeInboxTagsArray([
                      ...(oldThreadItemData.callRecord.tags || []),
                      threadItemTag,
                    ]),
                  },
                };
              }
              return oldThreadItemData;
            },
            isOptimistic: false,
            getThreadOnly: true,
          });
          break;
        case 'REMOVED':
          // update tags array in thread
          await upsertUnifiedThread({
            matchValues: {
              threadId: params.threadId,
              locationId,
            },
            updater: (oldThreadData) => {
              if (!oldThreadData) return oldThreadData;
              const latestTaggedEntity = params.latestTaggedEntity;
              if (latestTaggedEntity?.entityId) {
                return {
                  ...oldThreadData,
                  uniqueTags: oldThreadData.uniqueTags?.map((tag) =>
                    tag.tagId === latestTaggedEntity.tagId
                      ? {
                          entityId: latestTaggedEntity.entityId,
                          entityType: latestTaggedEntity.entityType,
                          tagId: latestTaggedEntity.tagId,
                          entityCreatedAt: latestTaggedEntity.entityCreatedAt || '',
                          appliedBy: latestTaggedEntity.appliedBy || APPLIED_BY_FALLBACK,
                        }
                      : tag
                  ),
                };
              } else {
                return {
                  ...oldThreadData,
                  uniqueTags: oldThreadData.uniqueTags?.filter((tag) => tag.tagId !== params.tagId) || [],
                };
              }
            },
            isOptimistic: false,
          });
          // update tags array in threadItem
          await upsertThreadItem({
            matchValues: {
              threadId: params.threadId,
              locationId,
              threadItemId: params.threadCallId || params.entityId,
            },
            updater: (oldThreadItemData) => {
              if (!oldThreadItemData) return oldThreadItemData;
              if (isSmsMessageItem(oldThreadItemData) && oldThreadItemData.smsMessage) {
                return {
                  smsMessage: {
                    ...oldThreadItemData.smsMessage,
                    tagsDetailed:
                      oldThreadItemData.smsMessage.tagsDetailed?.filter((tag) => tag.tagId !== params.tagId) || [],
                    tags: oldThreadItemData.smsMessage.tags?.filter((tagId) => tagId !== params.tagId) || [],
                  },
                };
              }
              if (isCallRecordItem(oldThreadItemData) && oldThreadItemData.callRecord) {
                return {
                  callRecord: {
                    ...oldThreadItemData.callRecord,
                    tags: oldThreadItemData.callRecord.tags?.filter((tag) => tag.tagId !== params.tagId) || [],
                  },
                };
              }
              return oldThreadItemData;
            },
            isOptimistic: false,
            getThreadOnly: true,
          });

          // Emit external sync event for voicemail tags
          // reopen the voicemail details panel to force refresh tag data
          if (params.entityType === EntityType.ENTITY_TYPE_VOICEMAIL && GlobalPanel.isOpen('voicemail-details')) {
            emitOpenVoicemailInsightPanel({
              voicemailId: params.entityId,
              locationId,
            });
          }
          break;
        default:
          break;
      }
    },
    [upsertUnifiedThread]
  );

  return wsEventHandler;
};

export const useInboxThreadItemTagWSSubscription = () => {
  const handler = useInboxThreadItemTagWSHandler();
  return useWebsocketEventSubscription(NotificationType.INBOX_THREAD_ITEM_TAG, handler);
};
